// Updated Quiz.js component with dashboard aesthetic matching QuizSession02
import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { updateSessionProgress } from "../../supabase";

// SVG Check Mark Component - Updated to match dashboard
const CheckMarkSVG = ({ size = 16, color = "#374151" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={color} 
    strokeWidth="3" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

const Quiz = ({ 
  showAnswers, 
  reviewMode = false, 
  onComplete, 
  resetQuiz, 
  requireCorrectAnswer = false 
}) => {
  const { user } = useAuth();
  const [score, setScore] = useState(0);
  const [timeTaken, setTimeTaken] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [feedback, setFeedback] = useState("");
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (resetQuiz) {
      console.log("🔄 Resetting quiz state");
      setQuizCompleted(false);
      setUserAnswers([]);
      setCurrentQuestionIndex(0);
      setStartTime(Date.now());
      setIsFinished(false);
      setScore(0);
      setTimeTaken(0);
      setFeedback("");
      setSaveError(null);
    }
  }, [resetQuiz]);

  const questions = [
    {
      question: "What is the best definition of chronic pain?",
      options: [
        "Pain that occurs immediately after an injury and goes away once healing begins.",
        "Pain that lasts for three months or longer even after the body has healed, often without signs of new tissue damage.",
        "Pain that is visible on medical imaging like MRIs or x-rays.",
        "Pain that only happens when there is active and ongoing tissue damage."
      ],
      correctAnswer: "Pain that lasts for three months or longer even after the body has healed, often without signs of new tissue damage.",
      explanation: "Chronic pain is defined as pain that persists for three months or longer, even after the body has healed. Unlike acute pain, which serves as a protective alarm, chronic pain can occur without ongoing tissue damage due to changes in the nervous system.",
      explanations: {
        "Pain that occurs immediately after an injury and goes away once healing begins.":
          "Try again. This describes acute pain, not chronic pain. Acute pain acts like a 'pain alarm' to signal immediate injury or threat.",
        "Pain that is visible on medical imaging like MRIs or x-rays.":
          "Try again. Chronic pain is often not visible on imaging. Diagnoses like burning mouth syndrome or idiopathic COFP typically don't show up on scans.",
        "Pain that only happens when there is active and ongoing tissue damage.":
          "Try again. In chronic pain, the 'pain alarm' is activated even without current tissue damage. The nerves can become overly sensitive and continue to signal pain without a new threat.",
        "Pain that lasts for three months or longer even after the body has healed, often without signs of new tissue damage.":
          "Correct! Pain that lasts for three months or longer even after the body has healed, often without signs of new tissue damage."
      }
    },
    {
      question: "Which of the following is a myth about chronic orofacial pain (i.e., an untrue statement)?",
      options: [
        "Pain always means there is harm or injury in the body.",
        "Chronic pain can continue even after the body has healed.",
        "Pain is processed through the nervous system and involves both mind and body.",
        "People with chronic pain can learn skills to participate in daily life despite their symptoms."
      ],
      correctAnswer: "Pain always means there is harm or injury in the body.",
      explanation: "This is a common myth about pain. In chronic pain conditions, the nervous system can continue to send pain signals even when there is no current tissue damage or injury. The pain is real, but it doesn't always indicate harm.",
      explanations: {
        "Pain always means there is harm or injury in the body.": "Correct! This is a myth. Chronic pain can occur even when there is no current injury, due to an overly sensitive alarm system.",
        "Chronic pain can continue even after the body has healed.": "Try again. This is actually true—chronic pain can persist even after healing.",
        "Pain is processed through the nervous system and involves both mind and body.": "Try again. Pain involves both mind and body, and is processed by the nervous system.",
        "People with chronic pain can learn skills to participate in daily life despite their symptoms.": "Try again. It is possible to learn strategies to live meaningfully even with pain."
      }
    },
    {
      question: "Which of the following is TRUE about chronic orofacial pain?",
      options: [
        "Chronic pain means there's no hope for relief.",
        "Pain will always prevent someone from living a normal life.",
        "Pain is only 'real' if there's visible damage on a scan.",
        "It is possible to manage chronic orofacial pain and improve quality of life."
      ],
      correctAnswer: "It is possible to manage chronic orofacial pain and improve quality of life.",
      explanation: "While chronic pain can be challenging, there are evidence-based treatments and strategies that can help people manage their symptoms and improve their quality of life. Recovery doesn't always mean being pain-free, but it can mean living well despite pain.",
      explanations: {
        "Chronic pain means there's no hope for relief.": "Try again. This is a myth—there is hope, and evidence-based treatments exist.",
        "Pain will always prevent someone from living a normal life.": "Try again. This is also a myth—people can regain function and participate in life.",
        "Pain is only 'real' if there's visible damage on a scan.": "Try again. This is a myth—pain can be real even without visible damage.",
        "It is possible to manage chronic orofacial pain and improve quality of life.": "Correct! This is not a myth—it's true that chronic pain can be managed with the right tools and support."
      }
    },
    {
      question: "What is one key benefit of practicing deep breathing for people experiencing chronic orofacial pain?",
      options: [
        "It increases muscle tension to prepare the body for action.",
        "It completely and instantly eliminates all pain.",
        "It helps relax tense muscles, reduce discomfort, and ease anxiety.",
        "It prevents all future pain from occurring."
      ],
      correctAnswer: "It helps relax tense muscles, reduce discomfort, and ease anxiety.",
      explanation: "Deep breathing activates the body's relaxation response, which can help reduce muscle tension, lower stress hormones, and calm the nervous system. This makes it an effective tool for managing chronic pain and anxiety.",
      explanations: {
        "It increases muscle tension to prepare the body for action.": "Try again. Deep breathing actually helps relax muscles, not tense them.",
        "It completely and instantly eliminates all pain.": "Try again. While helpful, deep breathing does not eliminate all pain instantly.",
        "It helps relax tense muscles, reduce discomfort, and ease anxiety.": "Correct! Deep breathing can reduce muscle tension, discomfort, and anxiety, making it a powerful mind-body tool.",
        "It prevents all future pain from occurring.": "Try again. Deep breathing can help manage pain, but it doesn't prevent all future pain."
      }
    },
    {
      question: "What does a 'false pain alarm' mean in the context of chronic orofacial pain?",
      options: [
        "The pain is not real and should be ignored.",
        "Pain signals fire without the presence of new tissue damage.",
        "The pain is only mental and not connected to the body.",
        "The pain is always predictable and constant."
      ],
      correctAnswer: "Pain signals fire without the presence of new tissue damage.",
      explanation: "In chronic pain, the nervous system can become hypersensitive and continue to send pain signals even when there's no current tissue damage. This is like a smoke alarm that goes off when you're cooking dinner - the alarm is working, but there's no actual fire (danger).",
      explanations: {
        "The pain is not real and should be ignored.": "Try again. The pain is real, but it does not indicate new harm or injury.",
        "Pain signals fire without the presence of new tissue damage.": "Correct! A 'false alarm' means pain is present without actual tissue damage, often due to an overactive nervous system.",
        "The pain is only mental and not connected to the body.": "Try again. While mental factors can influence pain, the pain is still physically experienced in the body.",
        "The pain is always predictable and constant.": "Try again. Pain in chronic conditions can fluctuate, making it unpredictable rather than constant."
      }
    }
  ];

  const handleFinishQuiz = async () => {
    // In review mode, just call onComplete to exit
    if (reviewMode) {
      if (onComplete) {
        onComplete();
      }
      return;
    }

    console.log('🔍 Starting quiz submission for Session 1...');
    if (!user) {
      console.error("❌ No user found for quiz submission");
      setSaveError("You must be logged in to submit quiz results!");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    const endTime = Date.now();
    const timeTakenInSeconds = (endTime - startTime) / 1000;

    let calculatedScore = 0;
    questions.forEach((question, index) => {
      if (userAnswers[index] === question.correctAnswer) {
        calculatedScore += 1;
      }
    });

    setScore(calculatedScore);
    setTimeTaken(Math.floor(timeTakenInSeconds));
    setIsFinished(true);
    setQuizCompleted(true);

    const quizData = {
      score: calculatedScore,
      answers: userAnswers,
      attempts: 1,
      totalQuestions: questions.length,
      timeTaken: Math.floor(timeTakenInSeconds)
    };

    const progressData = {
      quiz_completed: true,
      quiz_score: calculatedScore,
      session_completed: true,
      session_fully_completed: true,
      updated_at: new Date().toISOString()
    };

    console.log('🔍 Progress data to save:', progressData);
    console.log('🔍 Quiz data for callback:', quizData);

    try {
      console.log('🔍 Attempting to save quiz results for Session 1...');
      const result = await updateSessionProgress(user.id, 1, progressData);

      if (result) {
        console.log('✅ Quiz results saved successfully');
        
        try {
          await updateSessionProgress(user.id, 1, {
            quiz_answers: userAnswers,
            quiz_attempts: 1,
            quiz_completion_time: new Date().toISOString(),
            quiz_total_questions: questions.length,
            quiz_time_taken_seconds: Math.floor(timeTakenInSeconds)
          });
          console.log('✅ Extended quiz data saved successfully');
        } catch (extendedError) {
          console.warn('⚠️ Extended quiz data save failed, but core data saved:', extendedError);
        }

        if (onComplete) {
          console.log("✅ Quiz finished - calling onComplete callback");
          onComplete(quizData);
        }
      } else {
        throw new Error('Failed to save quiz results to database');
      }
    } catch (error) {
      console.error("❌ Error saving quiz result:", error);
      setSaveError(`Error saving quiz result: ${error.message}`);
      
      if (onComplete) {
        console.log("⚠️ Calling onComplete despite save error");
        onComplete(quizData);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAnswerSelection = (answer) => {
    const currentQuestion = questions[currentQuestionIndex];
    const updatedAnswers = [...userAnswers];
    updatedAnswers[currentQuestionIndex] = answer;
    setUserAnswers(updatedAnswers);
    
    // Show feedback based on the selected answer
    setFeedback(currentQuestion.explanations[answer]);
  };

  const handleNextQuestion = () => {
    // In requireCorrectAnswer mode, check if current answer is correct
    if (requireCorrectAnswer && !reviewMode) {
      const currentQuestion = questions[currentQuestionIndex];
      const selectedAnswer = userAnswers[currentQuestionIndex];
      
      if (!selectedAnswer || selectedAnswer !== currentQuestion.correctAnswer) {
        return; // Don't proceed if no answer selected or answer is incorrect
      }
    }

    setFeedback("");
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    setFeedback("");
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prevIndex) => prevIndex - 1);
    }
  };

  // Get current question data
  const currentQuestion = questions[currentQuestionIndex];
  const selectedAnswer = userAnswers[currentQuestionIndex];
  const isCorrectAnswer = selectedAnswer === currentQuestion.correctAnswer;

  // Determine if Next/Finish button should be enabled
  const canProceed = reviewMode || // Always can proceed in review mode
                    !requireCorrectAnswer || // Can proceed if not requiring correct answers
                    (selectedAnswer && isCorrectAnswer); // Can proceed if correct answer selected

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {!isFinished || reviewMode ? (
        <div>
          {/* Question Header - Updated styling to match dashboard */}
          <div style={{
            marginBottom: '20px'
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '8px',
              backgroundColor: '#F8F9FA',
              padding: '4px 8px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              display: 'inline-block'
            }}>
              Question {currentQuestionIndex + 1} of {questions.length}
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 20px 0',
              lineHeight: '1.4',
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
              {currentQuestion.question}
            </h3>
          </div>

          {/* Answer Options - Dashboard colors only, no red */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '24px'
          }}>
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === option;
              const isCorrect = option === currentQuestion.correctAnswer;
              
              let buttonColor = '#374151';
              let backgroundColor = '#FFFFFF';
              let borderColor = '#E5E7EB';
              let showCheckmark = false;

              if (reviewMode) {
                // Review mode: only highlight correct answers in green
                if (isCorrect) {
                  backgroundColor = '#ECFDF5';
                  borderColor = '#10B981';
                  buttonColor = '#374151';
                  showCheckmark = true;
                } else {
                  backgroundColor = '#FFFFFF';
                  borderColor = '#E5E7EB';
                  buttonColor = '#374151';
                }
              } else {
                // Normal mode: show selection and correct feedback
                if (isSelected) {
                  if (feedback) {
                    // Show feedback colors only for the selected answer
                    if (isCorrect) {
                      backgroundColor = '#ECFDF5';
                      borderColor = '#10B981';
                      buttonColor = '#374151';
                      showCheckmark = true;
                    } else {
                      // Incorrect answers get neutral gray styling - no red
                      backgroundColor = '#F8F9FA';
                      borderColor = '#D1D5DB';
                      buttonColor = '#374151';
                    }
                  } else {
                    // Just show selection in blue
                    backgroundColor = '#EFF6FF';
                    borderColor = '#3B82F6';
                    buttonColor = '#1B365D';
                  }
                } else {
                  // Default state for all unselected answers
                  backgroundColor = '#FFFFFF';
                  borderColor = '#E5E7EB';
                  buttonColor = '#374151';
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => !reviewMode && handleAnswerSelection(option)}
                  disabled={reviewMode}
                  onMouseDown={(e) => e.preventDefault()}
                  onSelectStart={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    padding: '16px 20px',
                    textAlign: 'left',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    lineHeight: '1.4',
                    minHeight: '60px',
                    color: buttonColor,
                    backgroundColor: backgroundColor,
                    border: `2px solid ${borderColor}`,
                    cursor: reviewMode ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!reviewMode && !isSelected) {
                      e.target.style.backgroundColor = '#F8F9FA';
                      e.target.style.borderColor = '#D1D5DB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!reviewMode && !isSelected) {
                      e.target.style.backgroundColor = '#FFFFFF';
                      e.target.style.borderColor = '#E5E7EB';
                    }
                  }}
                >
                  {showCheckmark && (
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#10B981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <CheckMarkSVG size={10} color="#FFFFFF" />
                    </div>
                  )}
                  <span 
                    style={{ 
                      flex: 1,
                      pointerEvents: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none'
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onSelectStart={(e) => e.preventDefault()}
                  >
                    {option}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Show explanation in review mode or after feedback */}
          {reviewMode && currentQuestion.explanation && (
            <div style={{
              padding: '16px',
              backgroundColor: '#F0F9FF',
              borderRadius: '12px',
              border: '1px solid #E0E7FF',
              marginBottom: '24px'
            }}>
              <div style={{ 
                fontWeight: '600', 
                marginBottom: '8px',
                color: '#374151',
                fontSize: '14px'
              }}>
                Why this is correct:
              </div>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.4'
              }}>
                {currentQuestion.explanation}
              </div>
            </div>
          )}

          {/* Feedback area - green for correct, light blue for incorrect */}
          {feedback && !reviewMode && (
            <div style={{
              padding: '16px',
              backgroundColor: feedback.includes("Correct") ? "#ECFDF5" : "#DBEAFE",
              borderRadius: '12px',
              border: feedback.includes("Correct") ? '1px solid #10B981' : '1px solid #60A5FA',
              marginBottom: '24px'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                lineHeight: '1.4',
                fontWeight: '500'
              }}>
                {feedback}
              </div>
            </div>
          )}

          {/* Navigation - Updated to match dashboard styling */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
          }}>
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              style={{
                backgroundColor: currentQuestionIndex === 0 ? '#F8F9FA' : '#FFFFFF',
                color: '#374151',
                border: '1px solid #E5E7EB',
                borderRadius: '24px',
                padding: '12px 24px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                opacity: currentQuestionIndex === 0 ? 0.5 : 1,
                boxShadow: currentQuestionIndex === 0 ? 'none' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}
            >
              Previous
            </button>

            {/* Progress Dots - Updated styling */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {questions.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: index === currentQuestionIndex ? '#1B365D' :
                      userAnswers[index] ? '#10B981' : '#E5E7EB',
                    border: '1px solid #E5E7EB',
                    transition: 'background-color 0.2s ease'
                  }}
                />
              ))}
            </div>

            {currentQuestionIndex < questions.length - 1 ? (
              <button
                onClick={handleNextQuestion}
                disabled={!canProceed}
                style={{
                  backgroundColor: canProceed ? '#1B365D' : '#F8F9FA',
                  color: canProceed ? '#FFFFFF' : '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: canProceed ? 'pointer' : 'not-allowed',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  opacity: canProceed ? 1 : 0.5,
                  boxShadow: canProceed ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleFinishQuiz}
                disabled={(!canProceed && !reviewMode) || isSaving}
                style={{
                  backgroundColor: (reviewMode || canProceed) ? '#1B365D' : '#F8F9FA',
                  color: (reviewMode || canProceed) ? '#FFFFFF' : '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '24px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: (reviewMode || canProceed) && !isSaving ? 'pointer' : 'not-allowed',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (reviewMode || canProceed) && !isSaving ? 1 : 0.5,
                  boxShadow: (reviewMode || canProceed) && !isSaving ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)' : 'none'
                }}
              >
                {isSaving && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #D1D5DB',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {isSaving ? 'Saving...' : reviewMode ? 'Finish Review' : 'Finish Quiz'}
              </button>
            )}
          </div>
        </div>
      ) : (
<div>
  {/* Quiz Complete Header */}
  <div style={{ 
    textAlign: 'center', 
    padding: '24px 0',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    marginBottom: '24px'
  }}>
    <div style={{
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      backgroundColor: '#ECFDF5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
      border: '1px solid #10B981'
    }}>
      <CheckMarkSVG size={24} color="#10B981" />
    </div>
    
    <h3 style={{
      fontSize: '18px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      Quiz Complete!
    </h3>
    
    {saveError && (
      <div style={{
        fontSize: '12px',
        color: '#374151',
        marginBottom: '12px',
        backgroundColor: '#F8F9FA',
        padding: '8px 12px',
        borderRadius: '8px',
        border: '1px solid #D1D5DB',
        display: 'inline-block'
      }}>
        {saveError}
      </div>
    )}
    
    <div style={{
      fontSize: '14px',
      color: '#6B7280',
      marginBottom: '16px',
      fontWeight: '500'
    }}>
      Score: {score} / {questions.length}
    </div>
    
    <div style={{
      backgroundColor: '#F8F9FA',
      color: '#374151',
      padding: '6px 12px',
      borderRadius: '16px',
      fontSize: '12px',
      fontWeight: '500',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      border: '1px solid #E5E7EB'
    }}>
      <CheckMarkSVG size={12} color="#374151" />
      {saveError ? 'Continuing despite save error' : 'Results saved successfully'}
    </div>
  </div>

  {/* Post-Assessment Section - Now shows immediately */}
  <div style={{
    padding: '20px',
    backgroundColor: '#EFF6FF',
    borderRadius: '12px',
    border: '1px solid #3B82F6'
  }}>
    <h5 style={{
      fontSize: '16px',
      fontWeight: '600',
      color: '#1B365D',
      margin: '0 0 8px 0',
      textAlign: 'center'
    }}>
      One More Step!
    </h5>
    <p style={{
      fontSize: '13px',
      color: '#1B365D',
      margin: '0 0 16px 0',
      opacity: 0.9,
      textAlign: 'center'
    }}>
      Complete the post-session assessment to unlock the next session
    </p>
    
    <button
      style={{
        backgroundColor: '#1B365D',
        color: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '24px',
        padding: '12px 20px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}
      onClick={() => {
        // This would trigger the assessment link opening
        // You'll need to pass the assessment handling functions as props to Quiz component
        if (window.parent && window.parent.handlePostAssessmentClick) {
          window.parent.handlePostAssessmentClick();
        }
      }}
    >
      Open Post-Session Assessment
    </button>
  </div>
</div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Remove all text selection and highlighting completely */
          *, *::before, *::after {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          /* Extra prevention for buttons and spans */
          button, button *, span, span *, div, div *, p, p * {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          /* Remove focus outlines and tap highlights */
          button:focus, button:active, div:focus, div:active {
            outline: none !important;
            -webkit-tap-highlight-color: transparent !important;
            box-shadow: none !important;
          }
          
          button::-moz-focus-inner {
            border: 0 !important;
            outline: 0 !important;
          }
          
          /* Remove all selection highlighting - multiple approaches */
          ::selection {
            background: transparent !important;
            color: inherit !important;
          }
          
          ::-moz-selection {
            background: transparent !important;
            color: inherit !important;
          }
          
          ::-webkit-selection {
            background: transparent !important;
            color: inherit !important;
          }
          
          /* Prevent weird browser highlighting on mobile and desktop */
          button:active, button:focus, span:active, span:focus {
            background-color: inherit !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          /* Prevent mouse selection events */
          button {
            -webkit-appearance: none !important;
            -moz-appearance: none !important;
            appearance: none !important;
            -webkit-user-drag: none !important;
            -khtml-user-drag: none !important;
            -moz-user-drag: none !important;
            -o-user-drag: none !important;
            user-drag: none !important;
          }
        `
      }} />
    </div>
  );
};

export default Quiz;